import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { EntityTypeUnion, TradingStrategy, UserPortfolio } from '@einhaus/project314-types';
import Button from '@/components/UI/Button';
import Callout from '@/components/UI/Callout';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import Modal from '@/components/UI/Modal';
import MultiSelect from '@/components/UI/MultiSelect';
import PortfolioSelect from '@/components/UI/PortfolioSelect';
import TradingStrategySelect from '@/components/UI/TradingStrategySelect';
import { trpc } from '@/api/trpc.ts';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import InactiveStrategyCallout from './InactiveStrategyCallout';

export type AddStrategyPortfolio = Pick<UserPortfolio, 'id' | 'name' | 'type' | 'strategiesSubscribed'>;

interface AddStrategyModalProps {
    onClose: () => void;
    /**
     * Portfolios the strategy can be added to. With one, the modal subscribes that portfolio; with
     * several, it asks which one, starting from `initialPortfolioId`.
     */
    portfolios: AddStrategyPortfolio[];
    /** Preselected portfolio; `null` leaves the choice to the user unless there is only one portfolio. */
    initialPortfolioId: number | null;
    /** Runs after the subscription is saved and before the modal closes. */
    onAdded?: (added: { portfolio: AddStrategyPortfolio; strategy: TradingStrategy }) => void;
}

const ALREADY_SUBSCRIBED_REASON = 'Already subscribed';

const ENTITY_TYPE_OPTIONS: { value: EntityTypeUnion['entityType']; label: string }[] = [
    { value: 'Asset', label: 'Assets' },
    { value: 'Cryptocurrency', label: 'Cryptocurrencies' }
];

/**
 * Subscribes a portfolio to a strategy it can consume and is not yet subscribed to. Strategies it already
 * has stay listed but cannot be chosen. Mount it only while open, so every opening starts from
 * `initialPortfolioId` with nothing else selected.
 */
const AddStrategyModal: React.FC<AddStrategyModalProps> = ({ onClose, portfolios, initialPortfolioId, onAdded }) => {
    const queryClient = useQueryClient();
    const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(
        () => initialPortfolioId ?? (portfolios.length === 1 ? (portfolios[0]?.id ?? null) : null)
    );
    const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
    const [selectedEntityTypes, setSelectedEntityTypes] = useState<EntityTypeUnion['entityType'][]>([]);

    const { strategies } = useTradingStrategies();

    const strategyAccessQuery = useAppQuery(
        {
            ...trpc.resourceAccess.listAccessibleResources.queryOptions({
                resourceType: 'tradingStrategy'
            }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load strategy access',
            showErrorToast: true
        }
    );

    const consumableStrategyIds = useMemo(
        () => new Set((strategyAccessQuery.data ?? []).filter((item) => item.canConsume).map((item) => item.resource.resourcePk)),
        [strategyAccessQuery.data]
    );

    const portfolioById = useMemo(() => new Map(portfolios.map((portfolio) => [portfolio.id, portfolio])), [portfolios]);

    const selectedPortfolio = selectedPortfolioId === null ? null : (portfolioById.get(selectedPortfolioId) ?? null);

    const subscribedStrategyIds = useMemo(
        () => new Set((selectedPortfolio?.strategiesSubscribed ?? []).map((strategy) => strategy.id)),
        [selectedPortfolio]
    );

    const selectedStrategy = useMemo(
        () => (selectedStrategyId === null ? null : (strategies.find((strategy) => strategy.id === selectedStrategyId) ?? null)),
        [selectedStrategyId, strategies]
    );

    const isSelectedStrategyInactive = selectedStrategy?.active === false;
    /** Live portfolios cannot subscribe to an inactive strategy; paper portfolios can, but it will not trade. */
    const isInactiveStrategyRejected = isSelectedStrategyInactive && selectedPortfolio?.type === 'live';

    const isPortfolioSelectable = useCallback((portfolio: { id: number }) => portfolioById.has(portfolio.id), [portfolioById]);

    const isStrategyConsumable = useCallback(
        (strategy: TradingStrategy) => consumableStrategyIds.has(strategy.id),
        [consumableStrategyIds]
    );

    // Listed rather than hidden, so a strategy the portfolio already has (possibly behind a filter elsewhere) is not a mystery.
    const getStrategyUnavailableReason = useCallback(
        (strategy: TradingStrategy) => (subscribedStrategyIds.has(strategy.id) ? ALREADY_SUBSCRIBED_REASON : null),
        [subscribedStrategyIds]
    );

    const handlePortfolioChange = useCallback(
        (portfolioId: number | null) => {
            setSelectedPortfolioId(portfolioId);

            // A strategy the newly chosen portfolio already subscribes to is no longer a valid choice.
            const nextPortfolio = portfolioId === null ? null : portfolioById.get(portfolioId);

            if (
                selectedStrategyId !== null &&
                nextPortfolio?.strategiesSubscribed?.some((strategy) => strategy.id === selectedStrategyId)
            ) {
                setSelectedStrategyId(null);
            }
        },
        [portfolioById, selectedStrategyId]
    );

    const { mutateAsync: addStrategy, isPending: isAddingStrategy } = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries(trpc.user.fetchPortfolio.pathFilter());
                void queryClient.invalidateQueries(trpc.user.fetchPortfolios.pathFilter());
            }
        }),
        {
            successMessage: 'Strategy added successfully',
            fallbackErrorMessage: 'Failed to add strategy'
        }
    );

    const canAdd =
        selectedPortfolio !== null &&
        selectedStrategy !== null &&
        selectedEntityTypes.length > 0 &&
        !isInactiveStrategyRejected &&
        !isAddingStrategy;

    const handleAddStrategy = useCallback(async () => {
        if (!canAdd || !selectedPortfolio || !selectedStrategy) return;

        try {
            const result = await addStrategy({
                portfolioId: selectedPortfolio.id,
                settingType: 'strategySubscribed',
                value: {
                    strategyId: selectedStrategy.id,
                    allowedUnderlyingEntityTypes: selectedEntityTypes
                }
            });

            if (!result.success) return;

            onAdded?.({ portfolio: selectedPortfolio, strategy: selectedStrategy });
            onClose();
        } catch {
            // The shared mutation wrapper displays the server's runtime-readiness error.
        }
    }, [addStrategy, canAdd, onAdded, onClose, selectedEntityTypes, selectedPortfolio, selectedStrategy]);

    return (
        <Modal isOpen title="Add Strategy to Portfolio" onClose={onClose}>
            <div className="space-y-4">
                {portfolios.length === 0 && (
                    <Callout title="You have no portfolios yet" variant="info">
                        <Link className="text-primary hover:text-blue-400" to="/user/portfolios">
                            Create a portfolio
                        </Link>{' '}
                        before adding strategies to it.
                    </Callout>
                )}

                {portfolios.length > 1 && (
                    <PortfolioSelect
                        filter={isPortfolioSelectable}
                        id="addStrategyPortfolioSelect"
                        label="Portfolio"
                        placeholder="Select a portfolio..."
                        value={selectedPortfolioId}
                        onChange={handlePortfolioChange}
                    />
                )}

                {strategyAccessQuery.isPending ? (
                    <DataLoadingSpinner text="Loading strategies..." />
                ) : strategyAccessQuery.isError ? (
                    <Callout title="Could not load the strategies you can use" variant="error">
                        Close this window and try again.
                    </Callout>
                ) : (
                    <>
                        <TradingStrategySelect
                            filter={isStrategyConsumable}
                            getUnavailableReason={getStrategyUnavailableReason}
                            helpText={selectedPortfolio ? undefined : 'Choose a portfolio first.'}
                            id="strategySelect"
                            isDisabled={!selectedPortfolio}
                            label="Strategy"
                            placeholder="Select a strategy..."
                            value={selectedStrategyId}
                            onChange={setSelectedStrategyId}
                        />

                        {selectedStrategy && isSelectedStrategyInactive && (
                            <InactiveStrategyCallout
                                strategyId={selectedStrategy.id}
                                variant={isInactiveStrategyRejected ? 'error' : 'warning'}
                            >
                                {isInactiveStrategyRejected
                                    ? 'Live portfolios can only subscribe to active strategies. Activate it first, then add it here.'
                                    : 'You can add it to this paper portfolio, but it cannot be switched on and will not open positions until the strategy is activated.'}
                            </InactiveStrategyCallout>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-zinc-300 mb-2" htmlFor="entityTypesSelect">
                                Entity Types
                            </label>
                            <MultiSelect
                                isMulti
                                className="w-full"
                                id="entityTypesSelect"
                                options={ENTITY_TYPE_OPTIONS}
                                placeholder="Select entity types..."
                                value={ENTITY_TYPE_OPTIONS.filter((option) => selectedEntityTypes.includes(option.value))}
                                onChange={(selected) => {
                                    if (Array.isArray(selected)) {
                                        setSelectedEntityTypes(selected.map((option) => option.value as EntityTypeUnion['entityType']));
                                    }
                                }}
                            />
                        </div>
                    </>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!canAdd} isLoading={isAddingStrategy} variant="primary" onClick={() => void handleAddStrategy()}>
                        Add Strategy
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AddStrategyModal;
